import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { school, formatLocation } from "@/data/schoolData";
import { useLeadModal } from "@/components/site/SiteContext";
import { trackEvent } from "@/services/analytics";

const links = [
  { label: "About", to: "/about", testId: "nav-about-link" },
  { label: "Instructors", to: "/instructors", testId: "nav-instructors-link" },
  { label: "Schedule", to: "/schedule", testId: "nav-schedule-link" },
  { label: "Reviews", to: "/reviews", testId: "nav-reviews-link" },
  { label: "Contact", to: "/contact", testId: "nav-contact-link" },
];

const lockedNavigationPaths = new Set(["/", "/programs", ...links.map((link) => link.to)]);

function NavigationLink({ link, mobile = false }) {
  const testId = mobile ? `mobile-${link.testId}` : link.testId;
  const className = mobile ? undefined : "nav-link";
  if (link.external) return <a href={link.to} className={className} target="_blank" rel="noreferrer" data-testid={testId}>{link.label}</a>;
  return <Link to={link.to} className={className} data-testid={testId}>{link.label}</Link>;
}

export function Header({ minimal = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { openLeadModal } = useLeadModal();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const schoolLocation = formatLocation(school);
  const brandMeta = [schoolLocation, school.establishedYear ? `Est. ${school.establishedYear}` : ""].filter(Boolean).join(" · ");
  const customLinks = (school.customization?.navigationLinks || []).filter((link) => link?.label && link?.href && !lockedNavigationPaths.has(link.href)).filter((link, index, collection) => collection.findIndex((candidate) => candidate.href === link.href) === index).map((link, index) => ({ label: link.label, to: link.href, testId: link.testId || `custom-nav-link-${index + 1}`, external: link.href.startsWith("https://") }));
  const secondaryLinks = [...links, ...customLinks];

  return (
    <header className={`site-header ${minimal ? "site-header-funnel" : ""} ${scrolled ? "is-scrolled" : ""}`} data-testid="site-header">
      <div className="header-inner">
        <Link to="/" className="brand-lockup" data-testid="nav-brand-logo">
          {school.branding?.logoUrl ? <img className="brand-logo" src={school.branding.logoUrl} alt={school.branding.logoAlt || `${school.name} logo`} data-testid="nav-brand-image" /> : <span className="brand-mark">{school.monogram}</span>}
          {!school.branding?.logoUrl || !school.branding?.logoContainsName ? <span className="brand-copy"><strong>{school.name}</strong>{brandMeta && <em>{brandMeta}</em>}</span> : null}
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation" data-testid="desktop-navigation">
          <Link to="/" className="nav-link" data-testid="nav-home-link-desktop">Home</Link>
          <div className="nav-dropdown">
            <button type="button" className="nav-link nav-dropdown-trigger" onClick={() => setProgramsOpen(!programsOpen)} aria-expanded={programsOpen} data-testid="nav-programs-menu-button">
              Programs <ChevronDown size={14} aria-hidden="true" />
            </button>
            {programsOpen && (
              <div className="nav-dropdown-menu" data-testid="nav-programs-menu">
                <Link to="/programs" data-testid="nav-all-programs-link">All programs</Link>
                {school.programs.map((program) => <Link key={program.slug} to={`/${program.slug}`} data-testid={`nav-program-${program.slug}-link`}>{program.name}</Link>)}
              </div>
            )}
          </div>
          {secondaryLinks.map((link) => <NavigationLink key={link.to} link={link} />)}
        </nav>
        <div className="header-actions">
          {!minimal && school.phone && <a className="header-phone" href={school.phoneHref} onClick={() => trackEvent("phone_clicked", { source: "header" })} data-testid="header-phone-link"><Phone size={14} aria-hidden="true" /> <span>{school.phone}</span></a>}
          {!minimal && <button type="button" className="button button-accent header-cta" onClick={() => openLeadModal("header")} data-testid="nav-free-week-button">Get Your Free Week Pass</button>}
          <button type="button" className="menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-expanded={mobileOpen} aria-controls="mobile-navigation" data-testid="mobile-menu-toggle" aria-label={mobileOpen ? "Close menu" : "Open menu"}>
            {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="mobile-nav" id="mobile-navigation" data-testid="mobile-navigation">
          <Link to="/" data-testid="nav-mobile-home-link">Home</Link>
          <button type="button" onClick={() => setProgramsOpen(!programsOpen)} aria-expanded={programsOpen} data-testid="nav-mobile-programs-button">Programs <ChevronDown size={15} aria-hidden="true" /></button>
          {programsOpen && <div className="mobile-program-links">{school.programs.map((program) => <Link key={program.slug} to={`/${program.slug}`} data-testid={`nav-mobile-program-${program.slug}-link`}>{program.name}</Link>)}</div>}
          {secondaryLinks.map((link) => <NavigationLink key={link.to} link={link} mobile />)}
          {!minimal && <>{school.phone && <a href={school.phoneHref} onClick={() => trackEvent("phone_clicked", { source: "header" })} data-testid="mobile-phone-link"><Phone size={15} aria-hidden="true" /> Call {school.phone}</a>}<button type="button" className="button button-accent mobile-cta" onClick={() => openLeadModal("mobile-navigation")} data-testid="mobile-free-week-button">Get Your Free Week Pass</button></>}
        </div>
      )}
    </header>
  );
}
